import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatDate as formatDateUtil } from '@/lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Copy, KeyRound, Terminal } from 'lucide-react';
import { SelectFilter, SearchInput, DateRangeFilter, ClearFilters, type Option } from '@/components/filters';
import { buildQuery, type DateRange } from '@/lib/filters';
import { StatTiles } from '@/components/StatTiles';
import { PageHeader } from '@/components/PageHeader';
import { Pagination } from '@/components/Pagination';
import { StatusBadge } from '@/components/StatusBadge';

interface License {
  id: string;
  key: string;
  name: string;
  type: string;
  status: string;
  billingModel?: string;
  activationStatus: string;
  activationCount: number;
  activationLimit: number;
  expiresAt?: number;
  endOfSale?: number;
  createdAt: number;
  customerId?: string;
  productId?: string;
  customerName?: string;
  productName?: string;
  policyId?: string;
  policyName?: string;
  features?: any;
  numCameras?: number | null;
}

// `features` is stored either as a plain array of names, or as
// {analytics:[...], numCameras:N}. Read both so nothing is lost on edit.
function splitEntitlements(raw: any): { names: string; cameras: string } {
  if (Array.isArray(raw)) return { names: raw.join(', '), cameras: '' };
  if (raw && typeof raw === 'object') {
    const names = Array.isArray(raw.analytics)
      ? raw.analytics.join(', ')
      : Object.keys(raw).filter((k) => k !== 'numCameras' && raw[k] === true).join(', ');
    const n = raw.numCameras ?? raw.num_cameras;
    return { names, cameras: n == null ? '' : String(n) };
  }
  return { names: raw ? String(raw) : '', cameras: '' };
}

// Inverse of splitEntitlements — emits the object shape only when a camera
// count is present, matching how policies store it.
function buildEntitlements(names: string, cameras: string): any {
  const list = names.trim() ? names.split(',').map((x) => x.trim()).filter(Boolean) : [];
  const cams = cameras.trim() === '' ? null : Number(cameras);
  if (cams !== null && !Number.isNaN(cams)) return { analytics: list, numCameras: cams };
  return list.length ? list : null;
}

interface LicensesProps {
  user: any;
}

function Licenses({ user: _user }: LicensesProps) {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [licenses, setLicenses] = useState<License[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // status
  const [typeFilter, setTypeFilter] = useState('all');
  const [billingFilter, setBillingFilter] = useState('all');
  const [customerFilter, setCustomerFilter] = useState('all');
  const [productFilter, setProductFilter] = useState('all');
  const [policyFilter, setPolicyFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 25;
  const [dateRange, setDateRange] = useState<DateRange>({});
  const [customers, setCustomers] = useState<Array<{ id: string; name: string }>>([]);
  const [products, setProducts] = useState<Array<{ id: string; name: string }>>([]);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  // Check for create param in URL to auto-open create dialog
  useEffect(() => {
    if (searchParams.get('create') === 'true') {
      setIsCreateDialogOpen(true);
      // Remove the param from URL after opening dialog
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editLicense, setEditLicense] = useState<{
    id: string; name: string; customerId: string; productId: string;
    billingModel: string; expiresAt: string; endOfSale: string;
    features: string; numCameras: string;
  } | null>(null);
  const [selectedLicense, setSelectedLicense] = useState<License | null>(null);
  const [copiedActivationCommand, setCopiedActivationCommand] = useState(false);
  const [newLicense, setNewLicense] = useState({
    name: '',
    policyId: '',
    type: 'STANDARD',
    activationLimit: 1,
    expiresAt: '',
  });
  const [policies, setPolicies] = useState<Array<{ id: string; name: string; licenseType?: string; maxActivations?: number }>>([]);

  useEffect(() => {
    fetchLicenses();
  }, [filter, typeFilter, billingFilter, customerFilter, productFilter, policyFilter, search, dateRange.from, dateRange.to, page]);

  // Any filter change invalidates the current page — otherwise you can land on
  // page 7 of a result set that now has two pages and see an empty table.
  useEffect(() => {
    setPage(1);
  }, [filter, typeFilter, billingFilter, customerFilter, productFilter, policyFilter, search, dateRange.from, dateRange.to]);

  useEffect(() => {
    fetchPolicies();
    fetchCustomersAndProducts();
  }, []);

  const fetchCustomersAndProducts = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';
      const headers = { Authorization: `Bearer ${token}` };
      const [cRes, pRes] = await Promise.all([
        fetch(`${apiUrl}/api/customers?limit=1000`, { headers }),
        fetch(`${apiUrl}/api/products?limit=1000`, { headers }),
      ]);
      if (cRes.ok) {
        const j = await cRes.json();
        setCustomers((j.data || []).map((c: any) => ({ id: c.id, name: c.name })));
      }
      if (pRes.ok) {
        const j = await pRes.json();
        setProducts((j.data || []).map((p: any) => ({ id: p.id, name: p.name })));
      }
    } catch (error) {
      console.error('Failed to fetch customers/products:', error);
    }
  };

  const clearFilters = () => {
    setFilter('all');
    setTypeFilter('all');
    setBillingFilter('all');
    setCustomerFilter('all');
    setProductFilter('all');
    setPolicyFilter('all');
    setSearch('');
    setDateRange({});
    setPage(1);
  };

  const fetchPolicies = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';
      const res = await fetch(`${apiUrl}/api/policies`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const json = await res.json();
        const list = json?.data?.policies ?? json?.policies ?? json?.data ?? [];
        setPolicies(Array.isArray(list) ? list : []);
      }
    } catch (error) {
      console.error('Failed to fetch policies:', error);
    }
  };

  const fetchLicenses = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';
      const qs = buildQuery({
        status: filter,
        type: typeFilter,
        billingModel: billingFilter,
        customerId: customerFilter,
        productId: productFilter,
        policyId: policyFilter,
        search,
        from: dateRange.from,
        to: dateRange.to,
        limit: PAGE_SIZE,
        page,
      });
      const response = await fetch(`${apiUrl}/api/licenses${qs}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        // New shape: { data: { licenses, total } }; tolerate the old bare array.
        setLicenses(data.data?.licenses ?? data.data ?? []);
        setTotal(data.data?.total ?? (data.data?.licenses ?? data.data ?? []).length);
      }
    } catch (error) {
      console.error('Failed to fetch licenses:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (timestamp?: number) => {
    if (!timestamp) return t('licenses.never');
    return formatDateUtil(timestamp);
  };

  const handleCreateLicense = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      const payload: any = {
        name: newLicense.name,
        policyId: newLicense.policyId,
        type: newLicense.type,
        activationLimit: newLicense.activationLimit,
      };

      if (newLicense.expiresAt) {
        payload.expiresAt = new Date(newLicense.expiresAt).getTime();
      }

      const response = await fetch(`${apiUrl}/api/licenses`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        setIsCreateDialogOpen(false);
        setNewLicense({ name: '', policyId: '', type: 'STANDARD', activationLimit: 1, expiresAt: '' });
        fetchLicenses();
      }
    } catch (error) {
      console.error('Failed to create license:', error);
    }
  };

  const openEditDialog = (license: License) => {
    setEditLicense({
      id: license.id,
      name: license.name,
      customerId: license.customerId || '',
      productId: license.productId || '',
      billingModel: license.billingModel || '',
      expiresAt: license.expiresAt ? new Date(license.expiresAt).toISOString().slice(0, 10) : '',
      endOfSale: license.endOfSale ? new Date(license.endOfSale).toISOString().slice(0, 10) : '',
      features: splitEntitlements(license.features).names,
      numCameras: splitEntitlements(license.features).cameras,
    });
    setIsEditDialogOpen(true);
  };

  const handleEditLicense = async () => {
    if (!editLicense) return;
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';
      const body: any = {
        name: editLicense.name,
        customerId: editLicense.customerId || null,
        productId: editLicense.productId || null,
      };
      if (editLicense.billingModel) body.billingModel = editLicense.billingModel;
      body.expiresAt = editLicense.expiresAt || null;
      body.endOfSale = editLicense.endOfSale || null;
      // Overrides whatever was inherited from the policy at creation time.
      body.features = buildEntitlements(editLicense.features, editLicense.numCameras);
      const response = await fetch(`${apiUrl}/api/licenses/${editLicense.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (response.ok) {
        setIsEditDialogOpen(false);
        setEditLicense(null);
        fetchLicenses();
      } else {
        const err = await response.json().catch(() => ({}));
        alert(err.error || 'Failed to update license');
      }
    } catch (error) {
      console.error('Failed to update license:', error);
    }
  };

  const handleViewLicense = (license: License) => {
    setSelectedLicense(license);
    setCopiedActivationCommand(false);
    setIsViewDialogOpen(true);
  };

  const getActivationCommand = (licenseKey: string) => {
    const apiUrl = import.meta.env.VITE_API_URL || 'https://api.airgapsuite.com';
    const packageName = 'airgapsuite-license-client_1.0.0_amd64.deb';
    const packageUrl = `${apiUrl}/api/downloads/builds%2F${packageName}`;

    return `wget -O ${packageName} "${packageUrl}"
sudo dpkg -i ${packageName}
sudo apt-get install -f -y
sudo license-verify --api-url "${apiUrl}" activate-license --key "${licenseKey}"
sudo systemctl enable --now license-manager
license-verify tui`;
  };

  const copyActivationCommand = async () => {
    if (!selectedLicense) return;

    try {
      await navigator.clipboard.writeText(getActivationCommand(selectedLicense.key));
      setCopiedActivationCommand(true);
      window.setTimeout(() => setCopiedActivationCommand(false), 2000);
    } catch (error) {
      console.error('Failed to copy activation command:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="w-full px-4 py-6 sm:px-6 lg:px-8">
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">{t('common.loading')}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6 px-4 py-6 sm:px-6 lg:px-8">
      <PageHeader
        eyebrow="License management"
        icon={KeyRound}
        title={t('licenses.title')}
        subtitle={t('licenses.subtitle')}
        actions={(
          <Button onClick={() => setIsCreateDialogOpen(true)}>
            {t('licenses.createLicense')}
          </Button>
        )}
      />

      {(() => {
        const now = Date.now();
        const soon = now + 30 * 86400000;
        return (
          <StatTiles stats={[
            { label: t('licenses.stats.total', { defaultValue: 'Total' }), value: total || licenses.length },
            { label: t('licenses.stats.active', { defaultValue: 'Active on this page' }), value: licenses.filter((l) => l.status === 'ACTIVE' && !(l.expiresAt && l.expiresAt < now)).length, color: 'text-success' },
            { label: t('licenses.stats.expiring', { defaultValue: 'Expiring on this page' }), value: licenses.filter((l) => l.expiresAt && l.expiresAt >= now && l.expiresAt < soon).length, color: 'text-amber-700 dark:text-amber-300' },
            { label: t('licenses.stats.expired', { defaultValue: 'Expired on this page' }), value: licenses.filter((l) => l.status === 'EXPIRED' || (l.expiresAt && l.expiresAt < now)).length, color: 'text-destructive' },
            { label: t('licenses.stats.subscriptions', { defaultValue: 'Subscriptions on page' }), value: licenses.filter((l) => l.billingModel === 'SUBSCRIPTION').length, color: 'text-primary' },
            { label: t('licenses.stats.oneTime', { defaultValue: 'One-time on page' }), value: licenses.filter((l) => l.billingModel === 'PERPETUAL').length, color: 'text-foreground' },
          ]} />
        );
      })()}

      <Card>
        <CardHeader>
          <CardTitle>{t('licenses.allLicenses')}</CardTitle>
          <CardDescription>
            {t('licenses.listDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="filter-bar">
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder="Search name, key, customer or product"
            />
            <SelectFilter
              label={t('licenses.status')}
              value={filter}
              onChange={setFilter}
              options={[
                { value: 'ACTIVE', label: 'Active' },
                { value: 'EXPIRED', label: 'Expired' },
                { value: 'REVOKED', label: 'Revoked' },
              ]}
            />
            <details className="advanced-filters">
              <summary>{t('licenses.moreFilters', { defaultValue: 'More filters' })}</summary>
              <div className="filter-extra">
                <SelectFilter
                  label={t('licenses.type')}
                  value={typeFilter}
                  onChange={setTypeFilter}
                  options={['TRIAL', 'STANDARD', 'PREMIUM', 'ENTERPRISE'].map((v) => ({ value: v, label: v }))}
                />
                <SelectFilter
                  label="Billing"
                  value={billingFilter}
                  onChange={setBillingFilter}
                  options={[
                    { value: 'SUBSCRIPTION', label: 'Subscription' },
                    { value: 'PERPETUAL', label: 'One-time' },
                  ]}
                />
                <SelectFilter
                  label="Customer"
                  value={customerFilter}
                  onChange={setCustomerFilter}
                  width="w-56"
                  options={customers.map((c): Option => ({ value: c.id, label: c.name }))}
                />
                <SelectFilter
                  label="Policy"
                  value={policyFilter}
                  onChange={setPolicyFilter}
                  options={policies.map((p): Option => ({ value: p.id, label: p.name }))}
                />
                <SelectFilter
                  label="Product"
                  value={productFilter}
                  onChange={setProductFilter}
                  width="w-56"
                  options={products.map((p): Option => ({ value: p.id, label: p.name }))}
                />
                <DateRangeFilter label="Issued" value={dateRange} onChange={setDateRange} />
              </div>
            </details>
            <ClearFilters onClick={clearFilters} />
          </div>
          {licenses.length === 0 ? (
            <div className="empty-state">
              <p>{t('licenses.noLicenses')}</p>
              <Button onClick={() => setIsCreateDialogOpen(true)}>{t('licenses.createLicense')}</Button>
            </div>
          ) : (
            <Table className="data-table stack-mobile rows-2line">
              <TableHeader>
                <TableRow>
                  <TableHead>{t('licenses.table.license', { defaultValue: 'License' })}</TableHead>
                  <TableHead>{t('licenses.table.key', { defaultValue: 'Key' })}</TableHead>
                  <TableHead>{t('licenses.typeBilling', { defaultValue: 'Type / billing' })}</TableHead>
                  <TableHead>{t('licenses.status')}</TableHead>
                  <TableHead>{t('licenses.table.activations')}</TableHead>
                  <TableHead>{t('licenses.table.expires')}</TableHead>
                  <TableHead className="text-right">{t('users.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {licenses.map((license) => {
                  const subline = [license.customerName, license.productName].filter(Boolean).join(' · ');
                  const meta = [
                    license.customerName,
                    t('licenses.rowActivations', {
                      defaultValue: '{{used}}/{{limit}} activations',
                      used: license.activationCount,
                      limit: license.activationLimit,
                    }),
                    t('licenses.rowExpires', { defaultValue: 'Expires {{date}}', date: formatDate(license.expiresAt) }),
                  ].filter(Boolean).join(' · ');
                  return (
                    <TableRow key={license.id}>
                      <TableCell className="cell-primary">
                        <div className="od-field">
                          <button type="button" className="table-name line" title={license.name} onClick={() => handleViewLicense(license)}>
                            {license.name}
                          </button>
                          {subline && <span className="line line-sub text-xs text-muted-foreground" title={subline}>{subline}</span>}
                        </div>
                      </TableCell>
                      <TableCell className="cell-meta text-xs text-muted-foreground">{meta}</TableCell>
                      <TableCell data-label={t('licenses.table.key', { defaultValue: 'Key' })}>
                        <button
                          type="button"
                          className="license-key rounded-sm bg-accent px-2 py-1 font-mono text-[11px] text-primary"
                          title={t('licenses.viewFullKey', { defaultValue: 'View full key for {{name}}', name: license.name })}
                          onClick={() => handleViewLicense(license)}
                        >
                          {license.key}
                        </button>
                      </TableCell>
                      <TableCell data-label={t('licenses.typeBilling', { defaultValue: 'Type / billing' })}>
                        <div className="od-field">
                          <span className="text-xs font-semibold">{license.type}</span>
                          <span className="text-xs text-muted-foreground">
                            {license.billingModel === 'PERPETUAL'
                              ? 'One-time'
                              : license.billingModel === 'SUBSCRIPTION'
                                ? 'Subscription'
                                : t('licenses.billingUnspecified', { defaultValue: 'Unspecified' })}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="cell-status"><StatusBadge status={license.status} /></TableCell>
                      <TableCell data-label={t('licenses.table.activations')}>
                        <span className="font-mono text-xs od-nowrap">{license.activationCount} / {license.activationLimit}</span>
                      </TableCell>
                      <TableCell data-label={t('licenses.table.expires')}>
                        <span className="text-xs od-nowrap">{formatDate(license.expiresAt)}</span>
                      </TableCell>
                      <TableCell className="cell-actions text-right od-nowrap">
                        <Button variant="ghost" size="sm" onClick={() => handleViewLicense(license)}>
                          {t('licenses.view')}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => openEditDialog(license)}>
                          Edit
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
          {/* `total` is the server-side count for the ACTIVE filter set, so the
              range stays correct while filtering. */}
          <Pagination page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} />
        </CardContent>
      </Card>

      {/* Create License Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('licenses.dialogs.create.title')}</DialogTitle>
            <DialogDescription>
              {t('licenses.dialogs.create.description')}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">{t('licenses.dialogs.create.licenseName')}</Label>
              <Input
                id="name"
                value={newLicense.name}
                onChange={(e) => setNewLicense({ ...newLicense, name: e.target.value })}
                placeholder="My License"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="policy">Policy *</Label>
              <Select
                value={newLicense.policyId}
                onValueChange={(value) => {
                  const p = policies.find((x) => x.id === value);
                  setNewLicense({
                    ...newLicense,
                    policyId: value,
                    type: p?.licenseType || newLicense.type,
                    activationLimit: p?.maxActivations ?? newLicense.activationLimit,
                  });
                }}
              >
                <SelectTrigger aria-label="Policy">
                  <SelectValue placeholder={policies.length ? 'Select a policy (required)' : 'No policies — create one first'} />
                </SelectTrigger>
                <SelectContent>
                  {policies.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}{p.licenseType ? ` (${p.licenseType})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Every license must be created from a policy.</p>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="type">{t('licenses.dialogs.create.licenseType')}</Label>
              <Select
                value={newLicense.type}
                onValueChange={(value) => setNewLicense({ ...newLicense, type: value })}
              >
                <SelectTrigger aria-label={t('licenses.dialogs.create.licenseType')}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="TRIAL">Trial</SelectItem>
                  <SelectItem value="STANDARD">Standard</SelectItem>
                  <SelectItem value="PREMIUM">Premium</SelectItem>
                  <SelectItem value="ENTERPRISE">Enterprise</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="activationLimit">{t('licenses.dialogs.create.activationLimit')}</Label>
              <Input
                id="activationLimit"
                type="number"
                min="1"
                value={newLicense.activationLimit}
                onChange={(e) => setNewLicense({ ...newLicense, activationLimit: parseInt(e.target.value) || 1 })}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="expiresAt">{t('licenses.dialogs.create.expirationDate')}</Label>
              <Input
                id="expiresAt"
                type="date"
                value={newLicense.expiresAt}
                onChange={(e) => setNewLicense({ ...newLicense, expiresAt: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleCreateLicense} disabled={!newLicense.name || !newLicense.policyId}>
              {t('licenses.createLicense')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View License Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selectedLicense?.name}</DialogTitle>
            <DialogDescription>
              {t('licenses.dialogs.view.description')}
            </DialogDescription>
          </DialogHeader>
          {selectedLicense && (
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label>{t('licenses.licenseKey')}</Label>
                <code className="relative rounded bg-muted px-3 py-2 font-mono text-sm break-all">
                  {selectedLicense.key}
                </code>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label>Customer</Label>
                  <div className="text-sm">{selectedLicense.customerName || '—'}</div>
                </div>
                <div className="grid gap-2">
                  <Label>Product</Label>
                  <div className="text-sm">{selectedLicense.productName || '—'}</div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label>{t('licenses.type')}</Label>
                  <Badge variant="outline">{selectedLicense.type}</Badge>
                </div>
                <div className="grid gap-2">
                  <Label>{t('licenses.status')}</Label>
                  <StatusBadge status={selectedLicense.status} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label>{t('licenses.table.activations')}</Label>
                  <p className="text-sm">{selectedLicense.activationCount} / {selectedLicense.activationLimit}</p>
                </div>
                <div className="grid gap-2">
                  <Label>{t('licenses.dialogs.view.activationStatus')}</Label>
                  <p className="text-sm">{selectedLicense.activationStatus}</p>
                </div>
              </div>
              <div className="grid gap-2">
                <Label>{t('licenses.table.expires')}</Label>
                <p className="text-sm">{formatDate(selectedLicense.expiresAt)}</p>
              </div>
              <div className="grid gap-2">
                <Label>{t('licenses.table.created')}</Label>
                <p className="text-sm">{formatDate(selectedLicense.createdAt)}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label>Policy</Label>
                  <p className="text-sm">{selectedLicense.policyName || '—'}</p>
                </div>
                <div className="grid gap-2">
                  <Label>Billing</Label>
                  <p className="text-sm">{selectedLicense.billingModel || '—'}</p>
                </div>
              </div>
              {/* Entitlements the device actually receives in the signed license. */}
              <div className="grid gap-2">
                <Label>Cameras</Label>
                <p className="text-sm">
                  {splitEntitlements(selectedLicense.features).cameras || (
                    <span className="text-muted-foreground">Not set</span>
                  )}
                </p>
              </div>
              <div className="grid gap-2">
                <Label>Features</Label>
                <p className="text-sm">
                  {splitEntitlements(selectedLicense.features).names || (
                    <span className="text-muted-foreground">None</span>
                  )}
                </p>
              </div>
              <div className="rounded-md border bg-muted/40">
                <div className="flex items-center justify-between border-b px-4 py-2">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <Terminal className="h-4 w-4" />
                    Install and activate on Ubuntu
                  </div>
                  <Button variant="ghost" size="sm" onClick={copyActivationCommand}>
                    <Copy className="h-4 w-4 mr-2" />
                    {copiedActivationCommand ? 'Copied' : t('common.copy')}
                  </Button>
                </div>
                <pre className="max-h-56 overflow-x-auto p-4 text-xs leading-6">
                  <code>{getActivationCommand(selectedLicense.key)}</code>
                </pre>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setIsViewDialogOpen(false)}>{t('common.close')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit License Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit License</DialogTitle>
            <DialogDescription>Assign customer/product, adjust dates and billing model.</DialogDescription>
          </DialogHeader>
          {editLicense && (
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="edit-lic-name">{t('licenses.name')}</Label>
                <Input id="edit-lic-name" value={editLicense.name}
                  onChange={(e) => setEditLicense({ ...editLicense, name: e.target.value })} />
              </div>
              <div className="grid gap-2">
                <Label>Customer</Label>
                <Select value={editLicense.customerId || 'none'}
                  onValueChange={(v) => setEditLicense({ ...editLicense, customerId: v === 'none' ? '' : v })}>
                  <SelectTrigger><SelectValue placeholder="No customer" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No customer</SelectItem>
                    {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Product</Label>
                <Select value={editLicense.productId || 'none'}
                  onValueChange={(v) => setEditLicense({ ...editLicense, productId: v === 'none' ? '' : v })}>
                  <SelectTrigger><SelectValue placeholder="No product" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No product</SelectItem>
                    {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Billing</Label>
                <Select value={editLicense.billingModel || 'unset'}
                  onValueChange={(v) => setEditLicense({ ...editLicense, billingModel: v === 'unset' ? '' : v })}>
                  <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unset">—</SelectItem>
                    <SelectItem value="SUBSCRIPTION">Subscription</SelectItem>
                    <SelectItem value="PERPETUAL">One-time</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label>Expires</Label>
                  <Input type="date" value={editLicense.expiresAt}
                    onChange={(e) => setEditLicense({ ...editLicense, expiresAt: e.target.value })} />
                </div>
                <div className="grid gap-2">
                  <Label>End of sale</Label>
                  <Input type="date" value={editLicense.endOfSale}
                    onChange={(e) => setEditLicense({ ...editLicense, endOfSale: e.target.value })} />
                </div>
              </div>
              <div className="grid gap-2">
                <Label>Features (comma-separated)</Label>
                <Input
                  value={editLicense.features}
                  onChange={(e) => setEditLicense({ ...editLicense, features: e.target.value })}
                  placeholder="live_view, recording, anpr"
                />
              </div>
              <div className="grid gap-2">
                <Label>Number of Cameras (blank = not set)</Label>
                <Input
                  type="number"
                  min={0}
                  value={editLicense.numCameras}
                  onChange={(e) => setEditLicense({ ...editLicense, numCameras: e.target.value })}
                  placeholder="16"
                />
                <p className="text-xs text-muted-foreground">
                  Inherited from the policy when the license was created. Editing here overrides it
                  for this license only — the device picks it up on its next poll.
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleEditLicense} disabled={!editLicense?.name}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default Licenses;
